import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Container,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Divider,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import {
  BillingClassification,
  BILLING_CLASSIFICATION_LABELS,
  ShiftReport,
  ShiftReportStatus,
  shiftReportService,
} from '../services/shiftReportService';

// FEAT-055 — Client (careseeker) shift-report review page (MVP-18.61, 18.62,
// 18.64). The client reviews a report that was SENT TO THEM FOR REVIEW, then
// approves / rejects / escalates. On approve, payment is CLIENT-INITIATED — the
// page is explicit that the client (not Careva) initiates payment. NO copy on
// this page implies Careva approves, pays, runs payroll, or employs anyone.

const STATUS_LABELS: Record<ShiftReportStatus, string> = {
  DRAFT: 'Draft',
  SUBMITTED: 'Sent to you for review',
  UNDER_REVIEW: 'Under review',
  APPROVED: 'Approved',
  REJECTED: 'Returned to caregiver',
  ESCALATED: 'Escalated for supervisor review',
};

const STATUS_COLORS: Record<
  ShiftReportStatus,
  'default' | 'info' | 'warning' | 'success' | 'error'
> = {
  DRAFT: 'default',
  SUBMITTED: 'info',
  UNDER_REVIEW: 'warning',
  APPROVED: 'success',
  REJECTED: 'error',
  ESCALATED: 'error',
};

const BILLING_OPTIONS: BillingClassification[] = ['BILL_ONLY', 'PAY_ONLY', 'PAY_AND_BILL'];

function formatDateTime(iso?: string | null): string {
  if (!iso) return 'Not recorded';
  return new Date(iso).toLocaleString('en-US', {
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  });
}

const ShiftReportReviewPage: React.FC = () => {
  const [searchParams] = useSearchParams();
  const reportId = searchParams.get('reportId');
  const bookingId = searchParams.get('bookingId');

  const [report, setReport] = useState<ShiftReport | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [actionError, setActionError] = useState<string | null>(null);
  const [acting, setActing] = useState(false);

  const [billing, setBilling] = useState<BillingClassification | ''>('');
  const [rejectOpen, setRejectOpen] = useState(false);
  const [rejectReason, setRejectReason] = useState('');
  const [escalateOpen, setEscalateOpen] = useState(false);
  const [escalateReason, setEscalateReason] = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      if (reportId) {
        const loaded = await shiftReportService.getById(reportId);
        setReport(loaded);
        if (loaded.billingClassification) setBilling(loaded.billingClassification);
      } else if (bookingId) {
        const reports = await shiftReportService.listByBooking(bookingId);
        const reviewable =
          reports.find((r) => r.status === 'SUBMITTED' || r.status === 'UNDER_REVIEW') ??
          reports[0] ??
          null;
        setReport(reviewable);
        if (reviewable?.billingClassification) setBilling(reviewable.billingClassification);
      } else {
        setError('No shift report specified.');
      }
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to load shift report');
    } finally {
      setLoading(false);
    }
  }, [reportId, bookingId]);

  useEffect(() => {
    load();
  }, [load]);

  const runAction = useCallback(
    async (fn: () => Promise<ShiftReport>) => {
      setActing(true);
      setActionError(null);
      try {
        const updated = await fn();
        setReport(updated);
        if (updated.billingClassification) setBilling(updated.billingClassification);
        return updated;
      } catch (e) {
        setActionError(e instanceof Error ? e.message : 'Action failed');
        return null;
      } finally {
        setActing(false);
      }
    },
    []
  );

  const checklistSummary = useMemo(() => {
    const items = report?.checklistItems ?? [];
    const done = items.filter((i) => i.status === 'DONE').length;
    const notDone = items.filter((i) => i.status === 'NOT_DONE').length;
    const na = items.filter((i) => i.status === 'NOT_APPLICABLE').length;
    return { done, notDone, na, total: items.length };
  }, [report]);

  const handleStartReview = () => {
    if (!report) return;
    runAction(() => shiftReportService.markUnderReview(report.id));
  };

  const handleApprove = () => {
    if (!report) return;
    runAction(() => shiftReportService.approve(report.id, billing || undefined));
  };

  const handleReject = async () => {
    if (!report) return;
    const updated = await runAction(() => shiftReportService.reject(report.id, rejectReason.trim()));
    if (updated) {
      setRejectOpen(false);
      setRejectReason('');
    }
  };

  const handleEscalate = async () => {
    if (!report) return;
    const updated = await runAction(() =>
      shiftReportService.escalate(report.id, escalateReason.trim())
    );
    if (updated) {
      setEscalateOpen(false);
      setEscalateReason('');
    }
  };

  const handleSetBilling = (value: BillingClassification) => {
    setBilling(value);
    if (!report) return;
    runAction(() => shiftReportService.setBillingClassification(report.id, value));
  };

  if (loading) {
    return (
      <Container maxWidth="md" sx={{ py: 6, textAlign: 'center' }}>
        <CircularProgress data-testid="shift-report-review-loading" />
      </Container>
    );
  }

  if (error || !report) {
    return (
      <Container maxWidth="md" sx={{ py: 4 }}>
        <Alert severity="error" data-testid="shift-report-review-error">
          {error ?? 'Shift report not found.'}
        </Alert>
      </Container>
    );
  }

  const isSubmitted = report.status === 'SUBMITTED';
  const isUnderReview = report.status === 'UNDER_REVIEW';
  const isApproved = report.status === 'APPROVED';
  const isRejected = report.status === 'REJECTED';
  const isEscalated = report.status === 'ESCALATED';
  const canDecide = isSubmitted || isUnderReview;

  return (
    <Container maxWidth="md" sx={{ py: 4 }} data-testid="shift-report-review-page">
      <Typography variant="h4" gutterBottom>
        Shift report review
      </Typography>
      <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
        This shift report was sent to you for review. Review the visit details below, then approve,
        request changes, or flag it for supervisor review.
      </Typography>

      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 2 }}>
            <Typography variant="h6">Visit summary</Typography>
            <Chip
              data-testid="shift-report-status"
              label={STATUS_LABELS[report.status]}
              color={STATUS_COLORS[report.status]}
            />
          </Stack>
          <Stack spacing={1}>
            <Typography variant="body2">
              <strong>Visit start:</strong> {formatDateTime(report.visitStartAt)}
            </Typography>
            <Typography variant="body2">
              <strong>Visit end:</strong> {formatDateTime(report.visitEndAt)}
            </Typography>
            <Typography variant="body2">
              <strong>Care tasks:</strong> {checklistSummary.done} done · {checklistSummary.notDone}{' '}
              not done · {checklistSummary.na} N/A
            </Typography>
            {report.careNotes ? (
              <Typography variant="body2">
                <strong>Care notes:</strong> {report.careNotes}
              </Typography>
            ) : null}
            {report.mileage ? (
              <Typography variant="body2">
                <strong>Mileage:</strong> {report.mileage}
              </Typography>
            ) : null}
          </Stack>
        </CardContent>
      </Card>

      {actionError ? (
        <Alert severity="error" sx={{ mb: 2 }} data-testid="shift-report-action-error">
          {actionError}
        </Alert>
      ) : null}

      {isSubmitted ? (
        <Card sx={{ mb: 3 }}>
          <CardContent>
            <Typography variant="body1" sx={{ mb: 2 }}>
              Open this report to begin your review.
            </Typography>
            <Button
              variant="contained"
              data-testid="start-review-button"
              disabled={acting}
              onClick={handleStartReview}
            >
              Start review
            </Button>
          </CardContent>
        </Card>
      ) : null}

      {canDecide ? (
        <Card sx={{ mb: 3 }}>
          <CardContent>
            <Typography variant="h6" gutterBottom>
              Billing classification
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              Optionally classify this visit for export to your external billing or payroll tools.
              Careva does not process payment.
            </Typography>
            <FormControl fullWidth size="small" sx={{ mb: 3 }}>
              <InputLabel id="billing-classification-label">Billing classification</InputLabel>
              <Select
                labelId="billing-classification-label"
                label="Billing classification"
                value={billing}
                data-testid="billing-classification-select"
                inputProps={{ 'data-testid': 'billing-classification-input' }}
                onChange={(e) => handleSetBilling(e.target.value as BillingClassification)}
                disabled={acting || !isUnderReview}
              >
                {BILLING_OPTIONS.map((option) => (
                  <MenuItem key={option} value={option} data-testid={`billing-option-${option}`}>
                    {BILLING_CLASSIFICATION_LABELS[option]}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            <Divider sx={{ mb: 2 }} />

            <Typography variant="h6" gutterBottom>
              Your decision
            </Typography>
            {!isUnderReview ? (
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                Start your review to approve, request changes, or escalate.
              </Typography>
            ) : null}
            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
              <Button
                variant="contained"
                color="success"
                data-testid="approve-button"
                disabled={acting || !isUnderReview}
                onClick={handleApprove}
              >
                Approve visit
              </Button>
              <Button
                variant="outlined"
                color="error"
                data-testid="reject-button"
                disabled={acting || !isUnderReview}
                onClick={() => setRejectOpen(true)}
              >
                Request changes
              </Button>
              <Button
                variant="outlined"
                data-testid="escalate-button"
                disabled={acting}
                onClick={() => setEscalateOpen(true)}
              >
                Contact supervisor
              </Button>
            </Stack>
          </CardContent>
        </Card>
      ) : null}

      {isApproved ? (
        <Card sx={{ mb: 3 }} data-testid="client-initiated-payment-panel">
          <CardContent>
            <Typography variant="h6" gutterBottom>
              Approved — payment is initiated by you
            </Typography>
            <Typography variant="body2" sx={{ mb: 1 }}>
              You approved this visit on {formatDateTime(report.reviewedAt)}. As the client, you
              initiate payment to your caregiver directly. Careva does not calculate, send, or
              approve payment on your behalf.
            </Typography>
            {report.billingClassification ? (
              <Typography variant="body2" data-testid="approved-billing-classification">
                <strong>Billing classification:</strong>{' '}
                {BILLING_CLASSIFICATION_LABELS[report.billingClassification]}
              </Typography>
            ) : null}
            <Button
              variant="contained"
              sx={{ mt: 2 }}
              data-testid="initiate-payment-button"
              href="/messages"
            >
              Arrange payment with your caregiver
            </Button>
          </CardContent>
        </Card>
      ) : null}

      {isRejected ? (
        <Alert severity="info" data-testid="rejected-panel">
          You returned this report to the caregiver for changes
          {report.rejectionReason ? `: "${report.rejectionReason}"` : '.'}
        </Alert>
      ) : null}

      {isEscalated ? (
        <Alert severity="warning" data-testid="escalated-panel">
          This visit was flagged for supervisor review. A supervisor will follow up.
        </Alert>
      ) : null}

      <Dialog open={rejectOpen} onClose={() => setRejectOpen(false)} data-testid="reject-dialog">
        <DialogTitle>Request changes</DialogTitle>
        <DialogContent>
          <DialogContentText sx={{ mb: 2 }}>
            Let the caregiver know what needs to change. This returns the report to them.
          </DialogContentText>
          <TextField
            autoFocus
            fullWidth
            multiline
            minRows={3}
            label="Reason"
            value={rejectReason}
            onChange={(e) => setRejectReason(e.target.value)}
            inputProps={{ 'data-testid': 'reject-reason-input' }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setRejectOpen(false)}>Cancel</Button>
          <Button
            variant="contained"
            color="error"
            data-testid="reject-confirm-button"
            disabled={acting || rejectReason.trim().length === 0}
            onClick={handleReject}
          >
            Send back to caregiver
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={escalateOpen}
        onClose={() => setEscalateOpen(false)}
        data-testid="escalate-dialog"
      >
        <DialogTitle>Contact supervisor</DialogTitle>
        <DialogContent>
          <DialogContentText sx={{ mb: 2 }}>
            Flag this visit for supervisor review. A supervisor will be notified.
          </DialogContentText>
          <TextField
            autoFocus
            fullWidth
            multiline
            minRows={3}
            label="What happened?"
            value={escalateReason}
            onChange={(e) => setEscalateReason(e.target.value)}
            inputProps={{ 'data-testid': 'escalate-reason-input' }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEscalateOpen(false)}>Cancel</Button>
          <Button
            variant="contained"
            data-testid="escalate-confirm-button"
            disabled={acting || escalateReason.trim().length === 0}
            onClick={handleEscalate}
          >
            Flag for supervisor review
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
};

export default ShiftReportReviewPage;
